/* eslint-disable no-param-reassign */
import styled from '@emotion/styled';
import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import Thumbnail from '@base/Thumbnail/Thumbnail';
import Link from '@base/Link/Link';
import Divider from '@base/Divider/Divider';
import H7 from '@base/H7/H7';
import H6 from '@base/H6/H6';
import AddToList from '@icon/AddToList';
import AddedToList from '@icon/AddedToList';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMeUid } from '@api/selectors/auth';
import Lock from '@icon/Lock';
import Image from '@base/Image';
import { getLinkPath, getLink } from '@models/Batch';
import { getCommaSeparatedUIDs } from '@utils/textHelper';
import useSubscribed from '@hooks/useSubscribed';
import { Spacings, mediaQuery } from '@styles/index';
import Tags from '@base/Tags/Tags';
import { segment } from '@actions/analytics';
import { evtNameCategoryMap } from '@constants/index';
import { SEARCH_RESULT_CLICKED, SEARCH_RESULT_PAGE } from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useAllTypesOfCourseEventProps from '@hooks/useAllTypesOfCourseEventProps';
import useIsRecordedGoal from '@hooks/useIsRecordedGoal';
import { EnrollButton, Container } from '../CourseCard';
import BatchCardInfo from './BatchCardInfo';
import { getBatchTag } from './util';

const CourseThumbnail = styled(Thumbnail)`
  border-top-left-radius: ${Spacings.SPACING_2B};
  border-top-right-radius: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoTablet} {
    width: 100%;
    height: 190px;
  }
`;

const CardContainer = styled(Container)`
  position: relative;
  display: block;
`;

const Content = styled.div`
  overflow: hidden;
  padding: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_14B};
  ${mediaQuery.uptoTablet} {
    margin-bottom: 0;
  }
`;

const TagsWrap = styled.div``;

const LanguageTag = styled(Tags)`
  width: fit-content;
  transition: all 300ms ease;
  margin-right: ${Spacings.SPACING_2B};
`;

const BatchCompletionTag = styled(Tags)`
  width: fit-content;
  transition: all 300ms ease;
  margin-right: ${Spacings.SPACING_2B};
`;
const CourseTitle = styled(H6)`
  margin-top: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_4B};
  color: var(--color-text-primary);
  font-size: ${({ $isFeedCard }) => ($isFeedCard ? '18px' : '14px')};
  ${mediaQuery.uptoTablet} {
    font-size: 16px;
  }
`;

const StyledDivider = styled(Divider)`
  margin: ${Spacings.SPACING_4B} auto;
`;

const TrialClassInfo = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  margin-bottom: ${({ $isFeedCard }) =>
    $isFeedCard ? Spacings.SPACING_2B : 0};
  h6 {
    margin-left: ${Spacings.SPACING_1B};
  }
`;

const CardFooter = styled.div`
  height: 56px;
  position: absolute;
  bottom: ${Spacings.SPACING_4B};
  width: 100%;
  ${mediaQuery.uptoTablet} {
    position: relative;
    bottom: unset;
  }
`;

const trialClassIconMap = {
  1: 'batch-live.svg',
  2: 'batch-play.svg',
  3: 'batch-play-all.svg'
};

const BatchCard = ({
  gridColumn,
  className,
  batch,
  toggleEnroll,
  orientation,
  firstAuthorUsername,
  dataAnalytics,
  index,
  type,
  updateRecentSearchLog,
  searchData,
  hideEnroll,
  isFeedCard,
  onClick
}) => {
  const dispatch = useDispatch();
  const batchGoalUID = batch?.goal?.uid || '';
  const meUID = useMyInfo(getMeUid);
  const { languages, name, trialClasses } = batch;
  const authors = batch.authors?.map((author) => ({
    ...author,
    name: author.name || `${author.firstName} ${author.lastName}`
  }));
  const isSubscribed = useSubscribed(batchGoalUID);
  const goalSegmentData = useGoalEventProps({ batchGoalUID });
  const segmentData = useAllTypesOfCourseEventProps({
    course: batch,
    isBatch: true
  });
  const isEnrolledFromStore = useSelector(
    (state) => batch?.uid && state.batches.data[batch?.uid]?.isEnrolled
  );
  const isEnrolledInBatch = (isEnrolledFromStore ?? batch.isEnrolled) || false;
  const isRecordedGoal = useIsRecordedGoal(batchGoalUID);

  const renderIconButton = useMemo(() => {
    let EnrollButtonIcon = null;
    if (!isSubscribed) {
      EnrollButtonIcon = Lock;
    } else if (isEnrolledInBatch) {
      EnrollButtonIcon = AddedToList;
    } else {
      EnrollButtonIcon = AddToList;
    }
    return <EnrollButtonIcon size={Spacings.SPACING_5B} />;
  }, [isEnrolledInBatch, isSubscribed]);

  if (!batch) return null;

  const handleEnroll = (e) => {
    e.stopPropagation();
    e.preventDefault();
    toggleEnroll(
      batch.uid,
      isEnrolledInBatch,
      batch,
      {
        ...goalSegmentData,
        ...segmentData
      },
      isRecordedGoal
    );
  };

  const handleAnalytics = () => {
    if (onClick) onClick();
    const batchUID = batch.uid || '';
    if (type === 'search') {
      const nSearchData = { ...searchData };
      delete nSearchData.query;
      const position = nSearchData.position ? nSearchData.position : 'See all';
      segment.track(
        SEARCH_RESULT_CLICKED,
        {
          ...goalSegmentData,
          ...nSearchData,
          sub_position: index + 1,
          educator_uid: getCommaSeparatedUIDs(authors),
          educator_username: getCommaSeparatedUIDs(authors, 'username'),
          educator_name: getCommaSeparatedUIDs(authors, 'name'),
          last_primary_source: SEARCH_RESULT_PAGE,
          last_primary_source_section: 'Batches',
          search_section: 'Global',
          current_screen: 'Batches',
          eventName: SEARCH_RESULT_CLICKED,
          eventType: 'click',
          version: 2,
          objectID: `${searchData.object_type}:${batchUID}`,
          userToken: meUID,
          ...nSearchData,
          position
        },
        evtNameCategoryMap.WITHOUT_CATEGORY
      );
      dispatch(
        updateRecentSearchLog({
          ...searchData,
          entity_id: `${searchData.object_type}:${batchUID}`,
          goal_uid: batchGoalUID
        })
      );
    }
  };

  const renderLanguages = () => {
    const languagesToShow =
      languages?.length > 3 ? languages.slice(0, 3) : languages;
    return languagesToShow?.map((language) => {
      return (
        <LanguageTag type="hollow" key={language.label}>
          {language.label}
        </LanguageTag>
      );
    });
  };

  return (
    <CardContainer
      className={className}
      gridColumn={gridColumn}
      orientation={orientation}
    >
      <Link href={getLinkPath()} as={getLink(batch)} onClick={handleAnalytics}>
        <CourseThumbnail
          imgUrl={batch.thumbnail || batch.coverPhoto}
          orientation={orientation}
          data-analytics={`${dataAnalytics}Card`}
        />
        {!hideEnroll && (
          <EnrollButton
            dropShadow
            onClick={isSubscribed ? handleEnroll : undefined}
            iconProps={{ hovDisabled: true }}
            isEnrolled={isEnrolledInBatch}
            disableHover={!isSubscribed}
            dataAnalytics={`${dataAnalytics}-courseEnroll`}
          >
            {renderIconButton}
          </EnrollButton>
        )}
        <Content $isFeedCard={isFeedCard}>
          <TagsWrap>
            {languages && renderLanguages()}
            <BatchCompletionTag type="hollow">
              {getBatchTag(batch)}
            </BatchCompletionTag>
          </TagsWrap>
          <CourseTitle lineClamp={2} as="h4" $isFeedCard={isFeedCard}>
            {name}
          </CourseTitle>
          <BatchCardInfo
            batch={batch}
            authors={authors}
            firstAuthorUsername={firstAuthorUsername}
          />
        </Content>
        <CardFooter>
          <StyledDivider />
          <TrialClassInfo $isFeedCard={isFeedCard}>
            <Image
              src={
                trialClassIconMap[trialClasses?.type] || 'batch-play-all.svg'
              }
              alt="play"
              width={16}
              height={16}
            />
            <H7
              color={
                trialClasses?.type === 1
                  ? 'var(--color-i-red)'
                  : 'var(--color-text-primary)'
              }
            >
              {trialClasses?.text || 'View full schedule'}
            </H7>
          </TrialClassInfo>
        </CardFooter>
      </Link>
    </CardContainer>
  );
};

export default BatchCard;

BatchCard.propTypes = {
  gridColumn: PropTypes.string,
  className: PropTypes.string,
  firstAuthorUsername: PropTypes.string,
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  batch: PropTypes.objectOf(PropTypes.any).isRequired,
  toggleEnroll: PropTypes.func.isRequired,
  dataAnalytics: PropTypes.string,
  index: PropTypes.number.isRequired,
  type: PropTypes.string,
  updateRecentSearchLog: PropTypes.func,
  searchData: PropTypes.objectOf(PropTypes.any),
  hideEnroll: PropTypes.bool,
  isFeedCard: PropTypes.bool,
  isEnrolled: PropTypes.bool
};

BatchCard.defaultProps = {
  gridColumn: 'span 6',
  className: '',
  orientation: 'vertical',
  firstAuthorUsername: '',
  dataAnalytics: '',
  type: '',
  updateRecentSearchLog: () => {},
  searchData: {},
  hideEnroll: false,
  isFeedCard: false,
  isEnrolled: false
};
